import {IconButton, Text} from '@chakra-ui/react';
import {Trans, useTranslation} from 'react-i18next';
import {useEffect, useState} from 'react';
import {State} from '@synako/halitrek';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {useHalState} from '@synako/haligator';
import {toDecimalFr, useLivraisonsPossiblesContext} from 'ff-common';
import {TypographyProps} from '@chakra-ui/system';
import {usePeutVoirMarges} from '@/hooks/auth/usePermissionsCommerciales';
import {useClient} from '@/haligator-factory';
import {CalculatriceIcon} from '@/components/svg/CalculatriceIcon';

interface MargesProduitProps {
  code_article: string;
  // Si fourni, les marges sont fetchées automatiquement via `marges-ligne-panier`.
  // Sinon, le picto calculatrice déclenche un fetch à la demande via `marges-article`.
  ligneDePanier?: State<LigneDePanierDesc>;
  // Désactive l'affichage du picto pour les articles non-FF (pas de marges sur marketplace).
  isItemCatalogueFF?: boolean;
  // Style du texte des marges (par défaut: xs / 500)
  fontSize?: TypographyProps['fontSize'];
  fontWeight?: string | number;
  // Taille du picto calculatrice (par défaut: 3 = 12px)
  iconSize?: number | string;
  /**
   * Prix unitaire en cours de saisie (via `useEditionPrixState`).
   * - `undefined` : pas d'édition → marges sans query preview
   * - `null` : saisie invalide / vide → garder la dernière réponse HAL valide
   * - `number` : preview serveur via `prix_dero_souhaite` (debounce ~300 ms)
   * Hors panier, une valeur définie (`null` ou `number`) charge aussi `marges-article`.
   */
  prixDraft?: number | null;
}

const DEBOUNCE_PREVIEW_MS = 300;

/**
 * Affiche les marges (VM / TM / MK) d'un article pour un commercial avec la permission `voir-les-marges`.
 *
 * - Si l'article est dans le panier (`ligneDePanier` fourni) : fetch automatique des marges.
 * - Sinon : affiche un picto calculatrice. Au clic, fetch à la demande des marges depuis le catalogue.
 * - Pendant la saisie d'un prix (`prixDraft`), refetch du lien parent
 *   (`marges-ligne-panier` ou `marges-article`) avec `prix_dero_souhaite`.
 *
 * Ne rend rien si l'utilisateur n'a pas la permission, ou si l'article n'est pas FF.
 */
export const MargesProduit: React.FC<MargesProduitProps> = ({
  code_article,
  ligneDePanier,
  isItemCatalogueFF = true,
  fontSize = '10px',
  fontWeight = '500',
  iconSize = '14px',
  prixDraft,
}) => {
  const {t} = useTranslation();
  const peutVoirMarges = usePeutVoirMarges();
  const clientNav = useClient();
  const {livraisonSelectionnee} = useLivraisonsPossiblesContext();

  // Debounce du draft valide ; saisie invalide = on garde le dernier prix previewé
  const [prixPreviewDebounced, setPrixPreviewDebounced] = useState<
    number | undefined
  >(undefined);
  useEffect(() => {
    if (prixDraft === undefined) {
      setPrixPreviewDebounced(undefined);
      return;
    }
    if (prixDraft === null) {
      return;
    }
    const timer = setTimeout(() => {
      setPrixPreviewDebounced(prixDraft);
    }, DEBOUNCE_PREVIEW_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [prixDraft]);

  // Hors panier : le picto (ou l'ouverture du crayon = prixDraft défini) déclenche le fetch.
  const [loadMargesArticle, setLoadMargesArticle] = useState<boolean>(false);
  useEffect(() => {
    if (prixDraft !== undefined && !ligneDePanier && isItemCatalogueFF) {
      setLoadMargesArticle(true);
    }
  }, [prixDraft, ligneDePanier, isItemCatalogueFF]);

  // Lien parent déjà templaté. On ne passe le prix que s'il est défini,
  // pour que l'URI de base reste exactement `…/marges` (celle que les commandes invalident).
  const suivreMarges = (prix_dero_souhaite?: number) => {
    if (!peutVoirMarges) {
      return undefined;
    }
    if (ligneDePanier) {
      return prix_dero_souhaite === undefined
        ? ligneDePanier.follow('marges-ligne-panier')
        : ligneDePanier.follow('marges-ligne-panier', {prix_dero_souhaite});
    }
    if (loadMargesArticle && isItemCatalogueFF) {
      return clientNav.follow('marges-article', {
        code_article,
        date_de_livraison: livraisonSelectionnee.data.tolede.date_de_livraison,
        ...(prix_dero_souhaite !== undefined ? {prix_dero_souhaite} : {}),
      });
    }
    return undefined;
  };

  const margesBaseState = useHalState(suivreMarges());
  const margesPreviewState = useHalState(
    prixPreviewDebounced !== undefined
      ? suivreMarges(prixPreviewDebounced)
      : undefined,
  );

  // Preview si dispo, sinon base (ou dernier preview encore en state pendant le refetch)
  const marges = margesPreviewState.data ?? margesBaseState.data;
  const margesAffichables =
    peutVoirMarges &&
    marges?.valeur_marge != undefined &&
    marges.taux_marge != undefined &&
    marges.marge_kg != undefined;
  // Picto calculatrice visible uniquement hors panier (FF), tant que les marges n'ont pas été chargées
  const showMargesIcon =
    peutVoirMarges && !ligneDePanier && isItemCatalogueFF && !margesAffichables;

  // Affichage des marges (VM / TM / MK) — réponse HAL (avec ou sans preview query)
  if (margesAffichables) {
    return (
      <Text
        fontSize={fontSize}
        color='gray.dark'
        fontWeight={fontWeight}
      >
        -{' '}
        <Trans
          i18nKey='back_office.devis_personnalises.marges_ligne'
          values={{
            vm: toDecimalFr(marges.valeur_marge, 2),
            tm: toDecimalFr(marges.taux_marge, 0),
            mk: toDecimalFr(marges.marge_kg, 2),
          }}
          components={{
            bold: (
              <Text
                as='span'
                fontWeight='800'
              />
            ),
          }}
        />
      </Text>
    );
  }

  // Picto calculatrice : déclenche le fetch des marges hors panier au clic (lazy load).
  if (showMargesIcon) {
    return (
      <IconButton
        aria-label={t('back_office.devis_personnalises.afficher_marges')}
        icon={<CalculatriceIcon boxSize={iconSize} />}
        variant='outline'
        size='xs'
        minW='auto'
        h='auto'
        borderColor='gray.light'
        borderRadius='4px'
        color='gray.dark'
        p='2px'
        _hover={{color: 'blue.main', bg: 'transparent'}}
        onClick={(e) => {
          // Dans le side panel remplacement, la vignette est un <Link> :
          // stopPropagation seul ne bloque pas la navigation.
          e.preventDefault();
          e.stopPropagation();
          setLoadMargesArticle(true);
        }}
      />
    );
  }

  return null;
};
